import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { CornerDownLeft, History, Loader2, Search, SearchX, X, type LucideIcon } from 'lucide-react';
import { SEARCH_MIN_CHARS, type SearchGroupType, type SearchHit } from '@nexops/shared';
import { useI18n } from '@/i18n/I18nProvider';
import { cn } from '@/shared/lib/cn';
import { Badge, Dialog, DialogContent, Skeleton } from '@/shared/ui';
import { useGlobalSearch } from './search.api';
import { GROUP_META, statusLabelKey, statusTone } from './search-meta';
import { useRecentSearches } from './useRecentSearches';
import { usePageHits } from './usePageHits';

interface PaletteItem {
  key: string;
  title: string;
  subtitle: string | null;
  to: string;
  icon: LucideIcon;
  color: string;
  status?: { label: string; tone: ReturnType<typeof statusTone> };
  /** Rekord z backendu — zapisywany do „ostatnio otwieranych”. */
  hit?: SearchHit;
}

interface PaletteSection {
  key: string;
  label: string;
  items: PaletteItem[];
  /** „Pokaż wszystkie (N)” — link do pełnej listy z przekazanym zapytaniem. */
  more?: { label: string; to: string };
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return v;
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Podświetla w tekście każde słowo zapytania (bez rozróżniania wielkości liter). */
function Highlight({ text, query }: { text: string; query: string }): ReactNode {
  const words = query.split(/\s+/).filter((w) => w.length >= 1);
  if (!words.length) return text;
  const parts = text.split(new RegExp(`(${words.map(escapeRe).join('|')})`, 'ig'));
  return parts.map((p, i) =>
    i % 2 === 1 ? (
      <mark key={i} className="rounded bg-primary/15 px-0.5 font-semibold text-foreground">
        {p}
      </mark>
    ) : (
      p
    ),
  );
}

const MORE_ROUTES: Partial<Record<SearchGroupType, string>> = { CUSTOMER: '/customers', LEAD: '/leads' };

export function SearchPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const { recent, push, clear, refresh } = useRecentSearches();
  const { pages, match } = usePageHits();

  const term = text.trim();
  const debounced = useDebounced(term, 200);
  const search = useGlobalSearch(debounced);
  const remote = term.length >= SEARCH_MIN_CHARS;

  useEffect(() => {
    if (open) {
      setText('');
      setActive(0);
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const hitItem = (hit: SearchHit): PaletteItem => {
    const meta = GROUP_META[hit.type];
    const key = hit.status ? statusLabelKey(hit.type, hit.status) : null;
    return {
      key: `${hit.type}:${hit.id}`,
      title: hit.title,
      subtitle: hit.subtitle,
      to: hit.to,
      icon: meta.icon,
      color: meta.color,
      status: hit.status && key ? { label: t(key), tone: statusTone(hit.status) } : undefined,
      hit,
    };
  };

  const sections = useMemo<PaletteSection[]>(() => {
    const out: PaletteSection[] = [];
    const pageItems = (list: ReturnType<typeof match>): PaletteItem[] =>
      list.map((p) => ({ key: `page:${p.key}`, title: p.title, subtitle: p.parent, to: p.to, icon: p.icon, color: p.color }));

    if (!term) {
      if (recent.length) out.push({ key: 'recent', label: t('search.recent'), items: recent.map(hitItem) });
      out.push({ key: 'quick', label: t('search.quick'), items: pageItems(pages.slice(0, 6)) });
      return out;
    }
    if (remote && debounced === term && search.data) {
      for (const g of search.data.groups) {
        const meta = GROUP_META[g.type];
        const base = MORE_ROUTES[g.type];
        out.push({
          key: g.type,
          label: t(meta.labelKey),
          items: g.hits.map(hitItem),
          more: base && g.total > g.hits.length ? { label: t('search.showAll', { count: g.total }), to: `${base}?q=${encodeURIComponent(term)}` } : undefined,
        });
      }
    } else if (remote && search.data && debounced !== term) {
      // Zapytanie się zmienia — pokaż poprzednie wyniki (placeholderData), żeby lista nie migała.
      for (const g of search.data.groups) out.push({ key: g.type, label: t(GROUP_META[g.type].labelKey), items: g.hits.map(hitItem) });
    }
    const pageMatches = match(term, 4);
    if (pageMatches.length) out.push({ key: 'pages', label: t('search.pages'), items: pageItems(pageMatches) });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term, remote, debounced, search.data, recent, pages, t]);

  const flat = useMemo(() => sections.flatMap((s) => s.items), [sections]);
  const flatKey = flat.map((i) => i.key).join('|');

  useEffect(() => setActive(0), [flatKey]);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const choose = (item: PaletteItem | undefined) => {
    if (!item) return;
    if (item.hit) push(item.hit);
    onOpenChange(false);
    navigate(item.to);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!flat.length) return;
      setActive((i) => (e.key === 'ArrowDown' ? (i + 1) % flat.length : (i - 1 + flat.length) % flat.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(flat[active]);
    }
  };

  const loading = remote && (search.isFetching || debounced !== term) && !search.data;
  const noResults = remote && !loading && !search.isFetching && debounced === term && flat.length === 0;
  const failed = remote && search.isError && debounced === term;

  let index = -1;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={t('topbar.search')} hideHeader hideClose closeLabel={t('common.close')} className="top-[9vh] max-w-2xl overflow-hidden p-0">
        <div className="-my-4 -mx-5">
          <div className="flex items-center gap-3 border-b border-border px-4">
            {search.isFetching && remote ? <Loader2 className="size-4 shrink-0 animate-spin text-primary" /> : <Search className="size-4 shrink-0 text-muted-foreground" />}
            <input
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={t('topbar.searchPlaceholder')}
              aria-label={t('topbar.search')}
              role="combobox"
              aria-expanded
              aria-controls="global-search-list"
              aria-activedescendant={flat[active] ? `gs-${active}` : undefined}
              autoComplete="off"
              spellCheck={false}
              className="h-14 min-w-0 flex-1 bg-transparent text-[15px] text-foreground outline-none placeholder:text-muted-foreground/70"
            />
            {text && (
              <button type="button" onClick={() => setText('')} aria-label={t('search.clear')} className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground">
                <X className="size-4" />
              </button>
            )}
            <kbd className="hidden rounded-md border border-border bg-muted px-1.5 py-0.5 font-sans text-2xs font-semibold text-muted-foreground sm:block">Esc</kbd>
          </div>

          <div ref={listRef} id="global-search-list" role="listbox" className="max-h-[min(60vh,28rem)] overflow-y-auto p-2">
            {loading && (
              <div className="space-y-2 p-1" aria-busy>
                {[0, 1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-11" />
                ))}
              </div>
            )}

            {failed && <p className="px-3 py-6 text-center text-sm text-danger">{t('search.error')}</p>}

            {noResults && !failed && (
              <div className="flex flex-col items-center gap-2 px-3 py-10 text-center">
                <SearchX className="size-8 text-muted-foreground/60" aria-hidden />
                <p className="text-sm font-semibold">{t('search.noResults', { query: term })}</p>
                <p className="text-xs text-muted-foreground">{t('search.noResultsHint')}</p>
              </div>
            )}

            {!loading &&
              sections.map((section) => (
                <div key={section.key} className="mb-1.5">
                  <div className="flex items-center justify-between px-2 pb-1 pt-2">
                    <span className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
                      {section.key === 'recent' && <History className="size-3" aria-hidden />}
                      {section.label}
                    </span>
                    {section.key === 'recent' && (
                      <button type="button" onClick={clear} className="text-[11px] text-muted-foreground hover:text-foreground">
                        {t('search.clearRecent')}
                      </button>
                    )}
                  </div>
                  {section.items.map((item) => {
                    index += 1;
                    const i = index;
                    const Icon = item.icon;
                    const isActive = i === active;
                    return (
                      <button
                        key={item.key}
                        id={`gs-${i}`}
                        data-index={i}
                        role="option"
                        aria-selected={isActive}
                        type="button"
                        onMouseMove={() => setActive(i)}
                        onClick={() => choose(item)}
                        className={cn(
                          'flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left transition-colors',
                          isActive ? 'bg-accent' : 'hover:bg-accent/60',
                        )}
                      >
                        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg" style={{ background: `${item.color}1f`, color: item.color }}>
                          <Icon className="size-4" aria-hidden />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[13.5px] font-semibold">
                            <Highlight text={item.title} query={section.key === 'recent' || section.key === 'quick' ? '' : term} />
                          </span>
                          {item.subtitle && (
                            <span className="block truncate text-xs text-muted-foreground">
                              <Highlight text={item.subtitle} query={section.key === 'recent' || section.key === 'quick' ? '' : term} />
                            </span>
                          )}
                        </span>
                        {item.status && <Badge tone={item.status.tone}>{item.status.label}</Badge>}
                        {isActive && <CornerDownLeft className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />}
                      </button>
                    );
                  })}
                  {section.more && (
                    <button
                      type="button"
                      onClick={() => {
                        onOpenChange(false);
                        navigate(section.more!.to);
                      }}
                      className="ml-2.5 mt-0.5 rounded-md px-2 py-1 text-xs font-semibold text-primary hover:bg-primary-soft"
                    >
                      {section.more.label}
                    </button>
                  )}
                </div>
              ))}
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-border bg-muted/40 px-4 py-2 text-[11px] text-muted-foreground">
            <span className="hidden gap-3 sm:flex">
              <span>
                <kbd className="font-sans font-semibold">↑↓</kbd> {t('search.hintNavigate')}
              </span>
              <span>
                <kbd className="font-sans font-semibold">Enter</kbd> {t('search.hintOpen')}
              </span>
            </span>
            <span className="ml-auto">{t('search.hintScope')}</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

